import React, { useState, useMemo, useEffect, useRef } from 'react';
import '../../css/ContentSidebar.css';
import { slugifySectionName } from '../../utils/slugify';
import { useData } from '../../state/DataContext';
import { useStudentProgress } from '../../state/StudentProgressContext';

function ContentSidebar({ courseType = "basic", onNavigate, currentSectionNumber, onClose }) {
  const { basicData, advancedData, loading: dataLoading, error: dataError } = useData(); 
  const { studentSections, loading: progressLoading, error: progressError } = useStudentProgress();
  const sidebarRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      const menuButton = document.querySelector('.menu-button-wrapper');
      if (sidebarRef.current && !sidebarRef.current.contains(event.target) && (!menuButton || !menuButton.contains(event.target))) {
        onClose && onClose();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  const chaptersData = useMemo(() => {
    if (courseType === "basic") return basicData || [];
    if (courseType === "advanced") return advancedData || [];
    return [];
  }, [courseType, basicData, advancedData]);

  const studentProgress = useMemo(
    () =>
      Object.fromEntries(
        (studentSections || []).map((s) => [s.sectionId, s.status])
      ),
    [studentSections]
  );

  // Build a flat list of all "section" items with their global index
  const globalSections = [];
  chaptersData.forEach((chapter, chapterIdx) => {
    chapter.sections.forEach((section, sectionIdx) => {
      if (section.type === "section") {
        globalSections.push({
          ...section,
          chapterIdx,
          sectionIdx,
          chapter,
        });
      }
    });
  });

  function getSectionSlug(globalSectionIdx, section) {
    const sectionNumber = String(globalSectionIdx + 1).padStart(2, "0");
    const slug = slugifySectionName(section.name);
    return `${sectionNumber}-${slug}`;
  }

  const firstIncompleteIdx = globalSections.findIndex(
    item => studentProgress[item.section_id] !== "completed"
  );

  const currentChapterIdx = useMemo(() => {
    if (!currentSectionNumber) return 0;
    let sectionCounter = 1;
    for (let c = 0; c < chaptersData.length; c++) {
      for (let s = 0; s < chaptersData[c].sections.length; s++) {
        if (chaptersData[c].sections[s].type === "section") {
          if (sectionCounter === currentSectionNumber) {
            return c;
          }
          sectionCounter++;
        }
      }
    }
    return 0;
  }, [chaptersData, currentSectionNumber]);

  const [openGroups, setOpenGroups] = useState(
    chaptersData.map((_, idx) => idx === currentChapterIdx)
  );

  useEffect(() => {
    setOpenGroups(chaptersData.map((_, idx) => idx === currentChapterIdx));
  }, [currentChapterIdx, chaptersData]);

  const handleGroupToggle = idx => {
    setOpenGroups(chaptersData.map((_, i) => i === idx));
  };

  if (dataLoading || progressLoading) return <div>Loading...</div>;
  if (dataError || progressError) return <div>{dataError || progressError}</div>;

  return (
    <div className="sidebar-content" ref={sidebarRef}>
      <div className="sidebar-title">
        {courseType === "basic" ? "Basic" : "Advanced"}
      </div>
      <div className="sidebar-list">
        {chaptersData.map((chapter, groupIdx) => (
          <div key={chapter.id || groupIdx} className="sidebar-group">
            <div
              className="sidebar-group-header"
              onClick={() => handleGroupToggle(groupIdx)}
            >
              <span>{chapter.title}</span>
              <span>
                <i
                  className={`bi bi-chevron-${
                    openGroups[groupIdx] ? "down" : "right"
                  }`}
                ></i>
              </span>
            </div>
            {openGroups[groupIdx] && (
              <ul className="sidebar-group-list">
                {chapter.sections.map((section, idx) => {
                  const globalIdx = globalSections.findIndex(
                    (item) =>
                      item.chapterIdx === groupIdx &&
                      item.sectionIdx === idx
                  );

                  let status;
                  if (studentProgress[section.section_id] === "completed") {
                    status = "completed";
                  } else if (globalIdx === firstIncompleteIdx) {
                    status = "current";
                  } else if (firstIncompleteIdx !== -1 && globalIdx < firstIncompleteIdx) {
                    status = "completed";
                  } else {
                    status = "locked";
                  }

                  const isClickable = status === "completed" || status === "current";

                  if (section.type === "section") {
                    const globalSectionIdx = globalSections.findIndex(
                      s => s.section_id === section.section_id
                    );
                    const sectionLabel = `Section ${String(globalSectionIdx + 1).padStart(2, "0")}`;
                    const isActive = (globalSectionIdx + 1) === currentSectionNumber;

                    return (
                      <li
                        key={section.section_id}
                        className={`sidebar-list-item ${status} ${isActive ? "active" : ""}`}
                        onClick={() => {
                          if (isClickable && onNavigate) {
                            const slug = getSectionSlug(globalSectionIdx, section);
                            onNavigate(slug);
                          }
                        }}
                      >
                        {status === "completed" ? (
                          <span className="sidebar-icon completed">✔</span>
                        ) : status === "current" ? (
                          <span className="sidebar-icon current">●</span>
                        ) : (
                          <span className="sidebar-icon locked">
                            <i className="bi bi-lock"></i>
                          </span>
                        )}
                        <span className="sidebar-section-label">{sectionLabel}</span>
                        <span className="sidebar-section-name">{section.name}</span>
                      </li>
                    );
                  }

                  if (section.type === "bonus") {
                    let icon;
                    if (status === "completed") {
                      icon = (
                        <span className="sidebar-icon completed">✔</span>
                      );
                    } else if (status === "current") {
                      icon = (
                        <span className="sidebar-icon bonus">
                          <i className="bi bi-star"></i>
                        </span>
                      );
                    } else {
                      icon = (
                        <span className="sidebar-icon locked">
                          <i className="bi bi-lock"></i>
                        </span>
                      );
                    }
                    return (
                      <li
                        key={section.section_id}
                        className={`sidebar-list-item bonus ${status}`}
                        onClick={() => {
                          if (isClickable && onNavigate)
                            onNavigate(chapter.id, { isBonus: true }); 
                        }}
                      >
                        {icon}
                        <span className="sidebar-section-label">
                          Bonus Article
                        </span>
                        <span className="sidebar-section-name">
                          {section.name}
                        </span>
                      </li>
                    );
                  }
                  return null;
                })}
              </ul>
            )}
            <hr className="sidebar-divider" />
          </div>
        ))}
      </div>
    </div>
  );
}

export default ContentSidebar;